// useCalendarEventMove.js — drag/drop calendar events to new dates/times

import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { useUser } from 'context/UserContext';
import { updateNode } from 'services/nodes/NodeWriteService';
import {
  applyOptimisticCalendarEventItems,
  applyOptimisticCalendarFieldValues,
  buildCalendarFieldValueAfterMove,
  buildCalendarOptimisticKey,
  getCalendarFieldSectionData,
  getCalendarFieldStorageKey,
} from 'views/calendar/shared/calendarFieldUpdate';

export default function useCalendarEventMove({
  typeDef,
  interactionMode = 'base',
  onEventMoved = null,
} = {}) {
  const { user } = useUser();
  const draggingItemRef = useRef(null);
  const [draggingItem, setDraggingItem] = useState(null);
  const [dragOverTargetKey, setDragOverTargetKey] = useState(null);
  const [optimisticValues, setOptimisticValues] = useState({});

  const showSaveToast = interactionMode === 'surface';

  const applyOptimisticNodes = useCallback((nodes) => (
    applyOptimisticCalendarFieldValues(nodes, optimisticValues, typeDef)
  ), [optimisticValues, typeDef]);

  const applyOptimisticEventItems = useCallback((eventItems) => (
    applyOptimisticCalendarEventItems(eventItems, optimisticValues, typeDef)
  ), [optimisticValues, typeDef]);

  const handleEventDragStart = useCallback((item, event) => {
    if (interactionMode === 'preview') return;
    if (!item?.node?.key || !item?.field?.name) return;
    if (item?.isRecurringOccurrence || item?.rawValue?.recurrence) {
      toast.info('Drag is disabled for recurring events. Open the series to reschedule.');
      return;
    }

    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', item.node.key);
    draggingItemRef.current = item;
    setDraggingItem(item);
  }, [interactionMode]);

  const handleEventDragEnd = useCallback(() => {
    draggingItemRef.current = null;
    setDraggingItem(null);
    setDragOverTargetKey(null);
  }, []);

  const handleDropTargetDragOver = useCallback((targetKey, event) => {
    if (!draggingItemRef.current && !draggingItem) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragOverTargetKey !== targetKey) {
      setDragOverTargetKey(targetKey);
    }
  }, [draggingItem, dragOverTargetKey]);

  const handleMoveToTarget = useCallback(async (targetDate, { targetHour = null, targetKey = '' } = {}) => {
    if (interactionMode === 'preview') return;
    const item = draggingItemRef.current || draggingItem;
    if (!item?.node?.key || !item?.field?.name) return;
    const nodeKey = item.node.key;
    const fieldName = item.field.name;
    const storageKey = getCalendarFieldStorageKey(item.node, item.field, typeDef?.key || item.node?.type);
    if (!storageKey) {
      toast.error('Could not move event: storage section unavailable.');
      handleEventDragEnd();
      return;
    }

    const nextValue = buildCalendarFieldValueAfterMove({
      fieldType: item.field.type,
      rawValue: item.rawValue,
      targetDate,
      targetHour,
      startPrecision: item.startPrecision,
    });

    if (!nextValue) {
      handleEventDragEnd();
      return;
    }

    const optimisticKey = buildCalendarOptimisticKey(nodeKey, storageKey, fieldName);
    const previousOptimistic = optimisticValues[optimisticKey];
    const currentSection = getCalendarFieldSectionData(item.node, storageKey);
    const previousFieldValue = currentSection?.[fieldName] ?? null;

    setOptimisticValues((prev) => ({
      ...prev,
      [optimisticKey]: nextValue,
    }));
    draggingItemRef.current = null;
    setDraggingItem(null);
    setDragOverTargetKey(null);

    try {
      await updateNode(nodeKey, {
        [storageKey]: {
          ...currentSection,
          [fieldName]: nextValue,
        },
      }, { actor: user });

      onEventMoved?.({
        item,
        nodeKey,
        fieldName,
        storageKey,
        nextValue,
      });

      if (showSaveToast) {
        toast.success('Date updated');
      }

      setOptimisticValues((prev) => {
        const next = { ...prev };
        delete next[optimisticKey];
        return next;
      });
    } catch (error) {
      setOptimisticValues((prev) => {
        const next = { ...prev };
        if (previousOptimistic !== undefined) {
          next[optimisticKey] = previousOptimistic;
        } else if (previousFieldValue !== undefined) {
          next[optimisticKey] = previousFieldValue;
        } else {
          delete next[optimisticKey];
        }
        return next;
      });

      toast.error(`Could not move event: ${error?.message || 'Unknown error'}`);
    }
  }, [
    draggingItem,
    handleEventDragEnd,
    interactionMode,
    onEventMoved,
    optimisticValues,
    showSaveToast,
    typeDef?.key,
    user,
  ]);

  return {
    applyOptimisticEventItems,
    applyOptimisticNodes,
    canDragEvents: interactionMode !== 'preview',
    dragOverTargetKey,
    draggingItem,
    handleDropTargetDragOver,
    handleEventDragEnd,
    handleEventDragStart,
    handleMoveToTarget,
  };
}
